import React from "react";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { SectionHeader } from "@/shell/components/SectionHeader";
import { useShadowParity } from "@/features/observability/hooks/use-shadow-parity";
import { ParitySummaryEntry } from "@/features/observability/types/parity.types";
import "@/features/observability/styles/projector-health.css";
import "@/features/observability/styles/shadow-parity.css";

const formatPct = (value: number): string => `${(value * 100).toFixed(2)}%`;

const formatWindow = (from: number, to: number): string =>
    `${new Date(from).toLocaleString()} → ${new Date(to).toLocaleString()}`;

const rowClass = (entry: ParitySummaryEntry): string => (entry.diverged > 0 || entry.error > 0 ? "row--critical" : "");

export function ShadowParityPage() {
    const { data, loading, error, lastUpdatedAt, refresh } = useShadowParity();
    const { summary, timeseries, divergences } = data;

    const hasSummaryData = summary !== null && summary.entries.length > 0;

    const refreshButton = (
        <button type="button" className="btn" onClick={refresh} disabled={loading}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                autorenew
            </span>
            {loading ? "Actualizando…" : "Auto · 30 s"}
        </button>
    );

    return (
        <Shell title="Shadow Parity" subtitle="Sistema · seguimiento del modo shadow">
            <div className="projector-health">
                <PageHeader
                    title="Shadow Parity"
                    lede="Seguimiento del modo comparación (shadow) de operations-read: ¿el camino nuevo da exactamente lo mismo que el viejo? Cuando la coincidencia sea estable al 100%, es seguro encender (cutover)."
                    right={refreshButton}
                />

                {summary !== null ? (
                    <div className="ph-live">
                        <span className="live-pulse">
                            <span className="dot" /> En vivo
                        </span>
                        <span className="sep" />
                        <span>Ventana {formatWindow(summary.windowFrom, summary.windowTo)}</span>
                        {lastUpdatedAt !== null ? (
                            <>
                                <span className="sep" />
                                <span>Última lectura hace {Math.max(0, Math.floor((Date.now() - lastUpdatedAt) / 1000))} s</span>
                            </>
                        ) : null}
                    </div>
                ) : null}

                {!hasSummaryData ? (
                    <div className="ph-empty" data-sev={error ? "crit" : "ok"}>
                        <span className="material-symbols-outlined">
                            {error ? "priority_high" : "hourglass_top"}
                        </span>
                        <div>
                            <b>{error ? "No se pudo cargar la paridad" : "Próximamente disponible"}</b>{" "}
                            <span>
                                {error
                                    ? "· el backend de Metrics no pudo leer los eventos de paridad. Reintenta o avisa a ingeniería."
                                    : "· esta vista se activará cuando empiece a haber datos de comparación. Activa el modo shadow y ejerce los listados para empezar a recolectar paridad."}
                            </span>
                        </div>
                    </div>
                ) : (
                    <>
                        <section className="section">
                            <SectionHeader
                                title="Resumen por endpoint"
                                count={summary.entries.length}
                                meta="matched · diverged · error · match rate"
                            />
                            <div className="tbl-wrap">
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Endpoint</th>
                                            <th className="td-num">Matched</th>
                                            <th className="td-num">Diverged</th>
                                            <th className="td-num">Error</th>
                                            <th className="td-num">Total</th>
                                            <th className="td-num">Match rate</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {summary.entries.map((entry) => (
                                            <tr key={entry.endpoint} className={rowClass(entry)}>
                                                <td className="td-strong mono">{entry.endpoint}</td>
                                                <td className="td-num">{entry.matched.toLocaleString()}</td>
                                                <td className="td-num">{entry.diverged.toLocaleString()}</td>
                                                <td className="td-num">{entry.error.toLocaleString()}</td>
                                                <td className="td-num">{entry.total.toLocaleString()}</td>
                                                <td className="td-num td-strong">{formatPct(entry.matchRate)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        <section className="section">
                            <SectionHeader
                                title="Match rate por hora"
                                count={timeseries?.buckets.length ?? 0}
                                meta="serie temporal de coincidencia"
                            />
                            {timeseries === null || timeseries.buckets.length === 0 ? (
                                <div className="sp-chart__empty">Sin muestras en la ventana.</div>
                            ) : (
                                <div className="tbl-wrap">
                                    <table className="tbl">
                                        <thead>
                                            <tr>
                                                <th>Hora</th>
                                                <th>Endpoint</th>
                                                <th className="td-num">Total</th>
                                                <th className="td-num">Match rate</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {timeseries.buckets.map((bucket) => (
                                                <tr key={`${bucket.endpoint}-${bucket.ts}`}>
                                                    <td>{new Date(bucket.ts).toLocaleString()}</td>
                                                    <td className="td-muted">{bucket.endpoint}</td>
                                                    <td className="td-num">{bucket.total.toLocaleString()}</td>
                                                    <td className="td-num td-strong">{formatPct(bucket.matchRate)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        <section className="section">
                            <SectionHeader
                                title="Divergencias"
                                count={divergences?.total ?? 0}
                                meta="muestras con diferencias entre primario y shadow"
                            />
                            {divergences === null || divergences.data.length === 0 ? (
                                <div className="ph-empty" data-sev="ok">
                                    <span className="material-symbols-outlined">check_circle</span>
                                    <div>
                                        <b>Sin divergencias registradas</b>{" "}
                                        <span>· el modo shadow está coincidiendo al 100% en esta ventana.</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="tbl-wrap">
                                    <table className="tbl">
                                        <thead>
                                            <tr>
                                                <th>Cuándo</th>
                                                <th>Endpoint</th>
                                                <th>Resultado</th>
                                                <th>Client</th>
                                                <th className="td-num">Filas (primario / shadow)</th>
                                                <th>Campos divergentes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {divergences.data.map((divergence) => (
                                                <tr
                                                    key={`${divergence.endpoint}-${divergence.ts}-${divergence.sampleOperationId ?? "n/a"}`}
                                                    className={divergence.outcome !== "matched" ? "row--critical" : ""}
                                                >
                                                    <td>{new Date(divergence.ts).toLocaleString()}</td>
                                                    <td className="td-muted">{divergence.endpoint}</td>
                                                    <td>{divergence.outcome}</td>
                                                    <td className="td-muted">{divergence.client ?? "—"}</td>
                                                    <td className="td-num">
                                                        {divergence.rowCountPrimary} / {divergence.rowCountShadow}
                                                    </td>
                                                    <td>
                                                        <div className="sp-fields">
                                                            {divergence.divergedFields.map((field) => (
                                                                <span key={field} className="sp-chip">
                                                                    {field}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </>
                )}
            </div>
        </Shell>
    );
}
